import type { ReactNode } from "react"
import Button from "@cloudscape-design/components/button"
import Icon from "@cloudscape-design/components/icon"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useNavigate } from "react-router-dom"
import type { DTADetails, VtmfFilingJob } from "../../../services/dtaApi"
import type { DTAVersion } from "../../../types/dta"
import WorkflowActions from "../../../components/Workflow/WorkflowActions"
import { useAuth } from "../../../contexts/AuthContext"

interface DataStreamActionsProps {
  stream: DTADetails
  dtaId: string
  versions: DTAVersion[]
  selectedVersion: string
  displayStage: string | undefined
  isDAE: boolean | undefined
  isVendor: boolean | undefined
  filings: VtmfFilingJob[]
  onAmendClick: () => void
  onSignClick: () => void
  onFileClick: () => void
  onRefresh: () => void
  /**
   * Surface a failed workflow transition to the page, which fires it as a toast (workflow errors
   * are transient events with no specific field to anchor to). The message may be a node (e.g. a
   * bulleted list of missing fields) so it renders as-is in the toast.
   */
  onWorkflowError?: (message: ReactNode) => void
  validateBeforePublish?: () => Array<{ field: string; tab: string; message: string }>
}

/**
 * Client-side Amend guards plus the tooltip. Stage eligibility and the max-active limit are owned
 * by the backend (create_version) and surface inline in the Amend modal when rejected.
 */
function getAmendState(
  versions: DTAVersion[],
  selectedVersion: string,
  displayStage: string | undefined,
): { disabled: boolean; reason: string } {
  const draft = versions.find((v) => v.workflow_stage === "DRAFT")
  if (draft) {
    return { disabled: true, reason: `An amendment is already in progress (v${draft.version})` }
  }
  if (displayStage === "DRAFT") {
    return { disabled: true, reason: "Already in Draft - edit directly" }
  }
  if (versions.length > 0 && selectedVersion !== versions[0].version) {
    return { disabled: true, reason: "Can only amend the latest version" }
  }
  if (displayStage === "REVIEW" || displayStage === "APPROVED") {
    return { disabled: false, reason: "Create a new minor version" }
  }
  return { disabled: false, reason: "Create a new major version" }
}

/**
 * Lifecycle action buttons for a data stream: Amend, EDC Submit Data, and the workflow CTA (Submit
 * for Review / Approve / Activate / Deactivate / Reactivate via WorkflowActions). Rendered in the
 * DataStreamHeader `actions` slot, right-aligned on the title/version/stage row. The workflow CTA
 * is the single primary-variant button; everything else is normal.
 *
 * Content/document actions (Reupload, Export, Save) live on the tab row (see
 * DataStreamContentActions).
 */
const DataStreamActions = ({
  stream,
  dtaId,
  versions,
  selectedVersion,
  displayStage,
  isDAE,
  isVendor,
  filings: _filings,
  onAmendClick,
  onSignClick: _onSignClick,
  onFileClick: _onFileClick,
  onRefresh,
  onWorkflowError,
  validateBeforePublish,
}: DataStreamActionsProps) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const amendState = getAmendState(versions, selectedVersion, displayStage)

  return (
    <SpaceBetween size="xs" direction="horizontal" alignItems="center">
      {isDAE && (
        <span title={amendState.reason}>
          <Button variant="normal" onClick={onAmendClick} disabled={amendState.disabled}>
            Amend DTA
          </Button>
        </span>
      )}
      {isDAE &&
        (stream.source_type === "edc" || stream.source_type === "sdtm") &&
        stream.ingestion_spec_id && (
          <Button variant="normal" onClick={() => navigate(`/dtas/${dtaId}/submit`)}>
            <Icon name="upload" size="normal" /> Submit Data
          </Button>
        )}
      {isVendor && dtaId && (
        <WorkflowActions
          dtaId={dtaId}
          currentVersion={selectedVersion}
          studyId={stream.study_id}
          vendorId={stream.vendor_id}
          currentStage={stream.workflow_stage || "DRAFT"}
          canTransition={true}
          transitionOptions={["REVIEW", "REVIEW", "APPROVED"]}
          userApproved={
            stream.approvals?.some(
              (a) => a.approver_id === user?.email && a.status === "APPROVED",
            ) ?? false
          }
          onActionComplete={onRefresh}
          onError={onWorkflowError}
          onSubmitData={() => navigate(`/vendor/submit/${dtaId}`)}
        />
      )}
      {isDAE &&
        dtaId &&
        displayStage &&
        (() => {
          // Forward transitions (DRAFT→REVIEW→APPROVED→ACTIVE) only allowed on latest version.
          // Deactivate/Reactivate allowed on any version that's ACTIVE or INACTIVE.
          const isLatest = !stream.latest_version || selectedVersion === stream.latest_version
          const canAct = isLatest || displayStage === "ACTIVE" || displayStage === "INACTIVE"

          return (
            <WorkflowActions
              dtaId={dtaId}
              currentVersion={selectedVersion}
              studyId={stream.study_id}
              vendorId={stream.vendor_id}
              currentStage={displayStage}
              canTransition={canAct}
              transitionOptions={["REVIEW", "APPROVED", "ACTIVE", "INACTIVE"]}
              userApproved={
                stream.approvals?.some(
                  (a) =>
                    a.approver_id === user?.email &&
                    a.status === "APPROVED" &&
                    a.stage === displayStage,
                ) ?? false
              }
              onActionComplete={onRefresh}
              onError={onWorkflowError}
              validateBeforePublish={validateBeforePublish}
            />
          )
        })()}
    </SpaceBetween>
  )
}

export default DataStreamActions
